import get from "lodash/get";
import pick from "lodash/pick";
import qs from "query-string";

import { RedirectionError } from "@lululemon/mwa-utils";
import * as atgApi from "@lululemon/ecom-common/src/services/atg";
import { stripWhitespace } from "@lululemon/ecom-common/src/util/helpers";
import { ACCEPTABLE_URL_PARAMETERS } from "./common";

const ACCEPTABLE_AFFILIATE_PARAMETERS = ["awc", "cid", "sv_campaign_id", "sv1"];
const ACCEPTABLE_REDIRECT_URL_PARAMETERS = [
  "bis-pdp",
  "color",
  "locale",
  "recieveEmailUpdates",
  "sl",
  "sz",
  "wishlist-pdp",
];

/**
 * @param path
 * @param queryString
 */
export const parseAtgUrl = (path = "", queryString = "") => {
  const parsed = qs.parse(queryString || "");
  let cleaned = qs.stringify(pick(parsed, ACCEPTABLE_URL_PARAMETERS));
  cleaned = cleaned ? `?${cleaned}` : "";

  return `${path || ""}${cleaned}`;
};

export function getDataUrl(path, queryString, nextLocale) {
  const atgUrl = parseAtgUrl(path, queryString)
    ?.replace(`/${nextLocale?.toLowerCase()}/`, "/")
    ?.replace(/\/+$/, "");
  return atgApi.getProductDetailUrl(atgUrl);
}

export const replaceRoute = (params, router, options) => {
  const { location } = router;
  const existing = qs.parse(location.search);
  const merged = { ...existing, ...params };

  router.replace(
    `${location.pathname}?${qs.stringify(merged)}`,
    undefined,
    options
  );
};

export const addSizeSelectionToQueryParam = (size = "", router) => {
  const { location } = router;
  const newSelectedSize = stripWhitespace(size);
  const queryParams = qs.parse(get(location, "search", {}));
  const sizeQueryParam = get(queryParams, "sz");

  if (sizeQueryParam !== newSelectedSize) {
    replaceRoute(
      {
        sz: newSelectedSize,
      },
      router,
      { scroll: false }
    );
  }
};

const isPathValid = (path) => path && typeof path === "string";

/**
 * Returns the productId from a PDP URL path
 *
 * @param {string} path - URL Path (e.g. /p/womens-leggings/Invigorate-HR-Tight-25/_/prod9750205)
 * @returns {string|null} productId
 */
export const getProductIdFromPath = (path = "") => {
  if (!isPathValid(path)) {
    return null;
  }

  const regexp = /p\/.+\/.+\/_\/(?<productId>\w+)/;
  return regexp.exec(path)?.groups?.productId ?? null;
};

/**
 * Returns the productUnifiedId from a PDP URL path
 *
 * @param {string} path - URL Path (e.g. /p/womens-leggings/Invigorate-HR-Tight-25/_/prod9750205)
 * @returns {string|null} productUnifiedId
 */
export const getProductUnifiedIdFromPath = (path = "") => {
  if (!isPathValid(path)) {
    return null;
  }

  const regexp = /p\/.+\/(?<unifiedId>[\w-]+)\/_\/.+/;
  return regexp.exec(path)?.groups?.unifiedId ?? null;
};

/**
 * @param url
 * @returns {string}
 */
export const parsePDPRedirectUrl = (url) => {
  if (!url) {
    return "";
  }
  const [path, queryString] = url.split(/[?#]/);
  const acceptableQueryParams = qs.stringify(
    pick(qs.parse(queryString || ""), ACCEPTABLE_REDIRECT_URL_PARAMETERS)
  );

  return `${path}?${acceptableQueryParams}`;
};

/** Comines path/query strings as supplied by QGL and next.
 * Strips unallowed params. Preserves relevant params that don't get served by gql
 * see CE2-2464
 * @param {string} redirectPath - Represents meta['redirect-path'] supplied by GQL
 * @param {string} queryString - Represents original query from ctx
 * @returns {string}
 */
export const parseRedirectUrl = (redirectPath = "", queryString = "") => {
  if (!redirectPath && !queryString) {
    return "";
  }

  const [path, redirectQueryString] = redirectPath.split(/[?#]/);
  const originalQueryString = queryString.replace("?", "");

  const allowedParams = new Set([
    ...ACCEPTABLE_REDIRECT_URL_PARAMETERS,
    ...ACCEPTABLE_AFFILIATE_PARAMETERS,
  ]);

  const queryParams = new URLSearchParams(
    `${redirectQueryString}&${originalQueryString}`
  );

  Array.from(queryParams.keys()).forEach((key) => {
    if (!allowedParams.has(key)) {
      queryParams.delete(key);
    }
  });

  const qp = queryParams.toString() ? `?${queryParams.toString()}` : "";

  return `${path}${qp}`;
};

export const getRedirectLoginReturnUrl = (
  urlPath = window.location.href,
  sku = "",
  skuModeName = ""
) => {
  if (!urlPath || !sku || !skuModeName) {
    return typeof urlPath === "string" ? urlPath : "";
  }
  const url = new URL(urlPath);
  const path = `${url.origin}${url.pathname}`;
  const queryStrings = url.search;
  const queryObject = new URLSearchParams(queryStrings);
  queryObject.append(skuModeName, sku);
  const href = `${path}?${queryObject.toString()}`;
  return encodeURIComponent(href);
};

export const redirectToLogin = (
  sku = "",
  mode = "wishlist-pdp",
  skuModeName = "wishlistSku"
) => {
  const loginBaseUrl = "/account/login";
  const returnUrl = getRedirectLoginReturnUrl(
    window.location.href,
    sku,
    skuModeName
  );
  window.location.assign(
    `${loginBaseUrl}?mode=${mode}&forceLogin=true&bvRedirectUrl=${returnUrl}`
  );
};

export const getProductRedirectQueryString = (
  productSummary = {},
  category = {}
) =>
  qs.stringify({
    productId: productSummary?.["product-id"] || "",
    unifiedId: productSummary?.["unified-id"] || "",
    product: encodeURIComponent(productSummary?.["display-name"] || ""),
    title: encodeURIComponent(productSummary?.title || ""),
    recModelId: "ProductModelV1",
    categoryName: category?.name || "",
  });

export const redirectException = (arg) => {
  const { message = "", url = "/error/network?component=product-detail-page" } =
    arg || {};
  return new RedirectionError({ message, url });
};
